import { useEffect, useState } from "react";

const BASE_URL = process.env.REACT_APP_API_URL || "http://localhost:8000/v1";

export enum APIErrorType {
    AUTHENTICATION_MISSING = "missing_auth_error",
    INVALID_TOKEN = "invalid_token_error",
    TOKEN_EXPIRED = "token_expired_error",
    GOOGLE_TOKEN_EXPIRED = "google_token_expired_error",
}

// We allow an arbitrary object to be passed in for the body
export type APIRequestInit = Omit<RequestInit, "body"> & {
    body?: RequestInit["body"] | Record<string, any>;
};

export class APIRequestError extends Error {
    public type: string;
    public status: number;

    constructor(status: number, type: string, message: string) {
        super(message);

        this.name = type;
        this.type = type;
        this.status = status;
    }
}

export const convertDatesInPlace = (obj: any) => {
    if (typeof obj !== "object") {
        return;
    }

    if (obj === undefined || obj === null) {
        return;
    }

    if (Array.isArray(obj)) {
        obj.forEach(convertDatesInPlace);
        return;
    }

    for (const [key, value] of Object.entries(obj)) {
        if (key === "requestBody") {
            // Do not convert values inside a `requestBody` as this is
            // for API logs and we want the raw request there.
            continue;
        }

        if (
            (key === "createdAt" || key === "updatedAt") &&
            typeof value === "string"
        ) {
            obj[key] = new Date(value);
            continue;
        }

        convertDatesInPlace(value);
    }
};

export async function fetchAPI<T>(
    path: string,
    init?: APIRequestInit,
): Promise<T> {
    init = init || {};

    init.mode = "cors";

    if (init.body && !(init.body instanceof FormData)) {
        if (typeof init.body !== "string") {
            init.body = JSON.stringify(init.body);
        }

        if (init.headers instanceof Headers) {
            if (!init.headers.has("Content-Type")) {
                init.headers.set("Content-Type", "application/json");
            }
        } else {
            const headers: Record<string, string> | undefined = Array.isArray(
                init.headers,
            )
                ? Object.fromEntries(init.headers)
                : init.headers;

            init.headers = {
                ...headers,
                "Content-Type": headers?.["Content-Type"] || "application/json",
            };
        }
    }

    const resp = await fetch(`${BASE_URL}${path}`, init as RequestInit);
    const value = await resp.json();

    if (resp.status >= 400) {
        throw new APIRequestError(
            resp.status,
            value.error.type,
            value.error.message,
        );
    }

    // Convert 'createdAt' and 'updatedAt' to dates
    convertDatesInPlace(value);

    return value;
}

// Will ensure that the returned value only changes once per 'timeout'
// across renders.
export const useDebouncedValue = (value: any, timeout: number) => {
    const [state, setState] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => setState(value), timeout);

        return () => clearTimeout(handler);
    }, [value, timeout]);

    return state;
};
